import React, { useState } from "react";
import toast from "react-hot-toast";

import {
  DialogProps,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Body1,
  DialogActions,
  DialogTrigger,
  Button,
  Spinner,
} from "@fluentui/react-components";

import { useAuth } from "@/context/auth";
import { useDocumentClassification } from "@/context/document-classification";

import { Playbook } from "@/types/risk-analysis";
import log from "@/utils/logger";

export interface PrivatizePlaybookDialogProps extends Omit<DialogProps, "children"> {
  playbook: Playbook;
}

export function PrivatizePlaybookDialog({ playbook, onOpenChange, ...props }: PrivatizePlaybookDialogProps) {
  const { user: auth } = useAuth();
  const { editPlaybook } = useDocumentClassification();

  const [loading, setLoading] = useState<boolean>(false);

  const isPrivate = !!playbook.ownerUserId && playbook.ownerUserId === auth.user.id;
  const handleUpdatePlaybook = async () => {
    try {
      setLoading(true);
      if (playbook.creatorUserId !== auth.user.id) {
        toast.error("You can't change the public status of a playbook you didn't create");
        return;
      }

      await editPlaybook(playbook.id, playbook.name, !isPrivate, playbook.editable, playbook.rules);
      onOpenChange?.(undefined, {
        open: false,
        type: "triggerClick",
        event: undefined,
      });
    } catch (error) {
      toast.error(
        error?.message || "Something went wrong while trying to update the playbook. Please try again later."
      );
      log.error("failed to privatize playbook", "PrivatizePlaybookDialog", { playbook });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} {...props}>
      <DialogSurface aria-labelledby={`update-${playbook.id}-title`} aria-describedby={`update-${playbook.id}-content`}>
        <DialogBody>
          <DialogTitle id={`update-${playbook.id}-title`}>Update Playbook</DialogTitle>
          <DialogContent id={`update-${playbook.id}-content`}>
            <Body1>
              Are you sure you want to make the playbook “<b>{playbook.name}</b>” {isPrivate ? "public" : "private"}?
            </Body1>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button disabled={loading} appearance="secondary">
                Cancel
              </Button>
            </DialogTrigger>
            <Button
              disabled={loading}
              appearance="primary"
              icon={loading ? <Spinner size="tiny" /> : null}
              onClick={handleUpdatePlaybook}
            >
              Update
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
